/**
 * The AnkhOS subscription plans — what a Service Provider pays US.
 *
 * ⚠️ NOT THE SAME AXIS AS `OPERATOR_TYPES`. The registration form already has a
 * field labelled "Provider tier", and it is ISP | VNO | OPERATOR — a LICENCE
 * classification that decides who may invoice pure internet. This file is the
 * COMMERCIAL band that decides what the provider is billed. Two different
 * things called "tier" one screen apart is a real reading hazard, which is why
 * the screen below never uses the bare word.
 *
 * # 🔑 THE BAND IS DERIVED FROM A MEASUREMENT, NEVER CHOSEN
 *
 * This is the single most important fact about this file, and it is why there
 * is no picker anywhere in it. `io.ankhos/templates/ankhos-tiers-v2.md`:
 *
 *   "The band is derived from a measurement, never self-declared. That is what
 *    makes it evidence rather than an assertion, and it is also why no model
 *    sits anywhere on this money path: nothing is chosen."
 *
 * A dropdown that let an applicant select "Launch" would be a self-declared
 * band — the exact thing the money path forbids — and it would look completely
 * ordinary on screen. The band is re-derived at each renewal from
 * `active_subscriber_count`, which is also where the downgrade rule comes from.
 *
 * So this module offers `bandFor(count)` and no setter.
 *
 * # ⚠️ THIS IS A MIRROR, AND MIRRORS DRIFT
 *
 * The authority is `io.ankhos/templates/ankhos-tiers-v2.md` at
 * `template_version: 2`, compiled by `compile.py` into `radbilling_component`
 * and `radbilling_component_price_rule` rows carrying a `catalog_ref`. The
 * price that actually reaches an invoice is resolved from those ROWS by
 * radbilling's `ResolvePrice` — never from this file.
 *
 * These numbers are here because the gateway cannot reach radbilling yet
 * (radaccess egress permits ns-radiusapi 3002-3008; radbilling listens on
 * 3014), so there is no route to fetch them from. When that route exists, this
 * table should be REPLACED by a fetch, not kept alongside one.
 *
 * Until then the screen says "indicative" and means it, and the tests assert
 * the structural invariants the template guarantees — contiguity, inclusive
 * boundaries, a negotiated top band, and that upgrading always beats staying on
 * overage — so an edit that breaks the arithmetic fails the build even though a
 * wrong PRICE cannot be caught here.
 */

/** Where the numbers come from, shown on screen so the reader can check them. */
export const TIER_SOURCE = {
  repo: "io.ankhos",
  file: "templates/ankhos-tiers-v2.md",
  templateId: "ankhos-tiers",
  templateVersion: 2,
  approvedOn: "2026-09-08",
  effectiveFrom: "2026-10-01",
} as const;

export const TIER_CURRENCY = "INR";

/**
 * ⛔ MONTHLY, and this is not a placeholder for "we also do annual".
 *
 * radbilling has ONE billing period and it is monthly (policy §10). Every plan
 * below also carries an annual rate, and that rate is **published but not
 * billable** — see `ANNUAL_IS_BILLABLE`. Conflating the two would put a price
 * on screen that no invoice can produce.
 */
export const TIER_BILLING_PERIOD = "MONTHLY";

/**
 * ⛔ FALSE, deliberately, and load-bearing for what the page may claim.
 *
 * The annual rates below are approved and publishable — an operator may ask for
 * one and it is arranged by hand — but nothing in the estate can raise an
 * annual invoice. The pricing page must say so beside the number rather than
 * present two equally-available options.
 */
export const ANNUAL_IS_BILLABLE = false;

/**
 * Policy §5 and Terms §2. Applied to the SUBSCRIPTION, not as a price rule — a
 * trial is not a discount, and modelling it as one would put a zero into the
 * price timeline that later has to be explained.
 *
 * 🔑 DAYS, not months, and the change of unit is itself the news. Until
 * 2026-09-08 this was three calendar months, chosen so the trial ended on a
 * billing boundary and the first invoice was a whole month. 45 days does not:
 * starting 1 March it ends 15 April, leaving a part-month stub.
 *
 * Terms §2 resolves the stub in the customer's favour — the first billing
 * period is the first WHOLE month beginning after the trial ends, and the days
 * in between are not charged. That is a contractual term, not a rounding
 * convention, so it is stated on the page rather than left to the invoice.
 *
 * ⚠️ The trial applies to EVERY plan, not only the entry band, and carries the
 * full Launch allowance of 500 subscribers.
 */
export const TIER_TRIAL_DAYS = 45;

/** The subscriber allowance during the trial — the full entry-plan allowance. */
export const TIER_TRIAL_SUBSCRIBERS = 500;

/**
 * Policy §3/§3.1. Named on screen because "subscribers" is ambiguous and the
 * ambiguity is worth about a quarter of the count: in one measured operator
 * subtree, 25% of plans are priced zero. Free and trial subscribers COUNT;
 * employees, admins, support users, API and service accounts do not.
 */
export const TIER_METRIC = "active_subscriber_count";

/**
 * 🔑 Counted across the WHOLE namespace subtree, at any depth. A sub-operator's
 * subscribers count toward its parent's band — which is the other half of
 * sub-operator nodes being free. Stating only "free sub-operators" without this
 * would read as a much larger giveaway than it is.
 */
export const TIER_AGGREGATION = "namespace_subtree";

/** The floor, in rupees per month. Below the entry allowance you pay this, not less. */
export const TIER_MINIMUM_MONTHLY = 1299;

export type TierBand = {
  readonly tier: string;
  readonly label: string;
  /** Inclusive. */
  readonly from: number;
  /** Inclusive. `null` means unbounded — the top band. */
  readonly to: number | null;
  /** Monthly, in INR. `null` on a negotiated band. This is the BILLABLE price. */
  readonly price: number | null;
  /** Per month when paid annually. Published, NOT billable — see `ANNUAL_IS_BILLABLE`. */
  readonly priceAnnual: number | null;
  /** Rupees per extra active subscriber beyond the allowance. NOT billable — no engine exists. */
  readonly overagePerSubscriber: number | null;
  readonly negotiated: boolean;
};

/**
 * ⛔ CHANGING A PRICE HERE DOES NOT CHANGE WHAT ANYONE IS BILLED, and that is
 * not a shortcoming of this file — it is the design. A price change is a new
 * template version with a later `effective_from` in the catalog, reviewed and
 * compiled, because an invoice raised last month must still resolve last
 * month's price.
 *
 * 🪤 `business` SURVIVED THE v1 → v2 RENAME BY NAME BUT NOT BY MEANING. It was
 * 1,001–2,000 subscribers at ₹3,499; it is now 4,001–10,000 at ₹13,999. Anyone
 * comparing the two ladders by tier name will read that as a 4× price rise on
 * the same product, and it is not — it is a different rung.
 */
export const TIER_BANDS: readonly TierBand[] = [
  { tier: "launch",   label: "Launch",   from: 0,     to: 500,   price: 1299,  priceAnnual: 999,   overagePerSubscriber: 2.4,  negotiated: false },
  { tier: "operate",  label: "Operate",  from: 501,   to: 1500,  price: 2999,  priceAnnual: 2499,  overagePerSubscriber: 1.9,  negotiated: false },
  { tier: "grow",     label: "Grow",     from: 1501,  to: 4000,  price: 6499,  priceAnnual: 5416,  overagePerSubscriber: 1.55, negotiated: false },
  { tier: "business", label: "Business", from: 4001,  to: 10000, price: 13999, priceAnnual: 11666, overagePerSubscriber: 1.3,  negotiated: false },
  { tier: "carrier",  label: "Carrier",  from: 10001, to: 25000, price: 27999, priceAnnual: 23332, overagePerSubscriber: 1.0,  negotiated: false },
  // The floor is 25,001, not "25,000+". Prose saying "25,000+" would put 25,000
  // in two bands at once, and the catalog validator rejects that as an overlap.
  { tier: "enterprise", label: "Enterprise", from: 25001, to: null, price: null, priceAnnual: null, overagePerSubscriber: null, negotiated: true },
];

/**
 * What is included at every plan, without exception. Rendered as its own block
 * rather than repeated per column: a row of five identical ticks reads as a
 * feature comparison, when the point is that there is nothing to compare.
 */
export const INCLUDED_EVERYWHERE: readonly string[] = [
  "Unlimited operator logins",
  "Unlimited sub-operators",
  "Every module of the platform you are licensed for",
  "Data export at any time",
];

/**
 * ⛔ PUBLISHED SHAPES, NOT QUOTABLE PRICES, for three of the four.
 *
 * Only the reseller module has numbers. "By managed device count" and "flat plus
 * per-transaction" are pricing *shapes* — putting them in a price column beside
 * a real figure would make them look quotable, so the page must render the
 * absence rather than a dash that reads as zero.
 */
export type Module = {
  readonly key: string;
  readonly name: string;
  readonly blurb: string;
  /** `null` where the shape is known but the number is not. */
  readonly priceEntry: number | null;
  readonly priceMid: number | null;
  readonly priceTop: number | null;
  /** Shown instead of prices when they are `null`. */
  readonly basis: string | null;
};

export const MODULES: readonly Module[] = [
  {
    key: "reseller",
    name: "Reseller & Channel",
    blurb: "Branded sub-operator portals, settlement and commission management.",
    priceEntry: 999, priceMid: 2499, priceTop: 4999, basis: null,
  },
  {
    key: "radius",
    name: "RADIUS / AAA and provisioning",
    blurb: "Authentication, authorisation and accounting for your network estate.",
    priceEntry: null, priceMid: null, priceTop: null, basis: "By managed device count",
  },
  {
    key: "ai",
    name: "AI operations",
    blurb: "Assisted diagnosis and operational answers over your own data.",
    priceEntry: null, priceMid: null, priceTop: null, basis: "+20% on the plan price",
  },
  {
    key: "payments",
    name: "Payment gateway and settlement",
    blurb: "Collect from your subscribers and reconcile settlements.",
    priceEntry: null, priceMid: null, priceTop: null, basis: "Flat fee plus per transaction",
  },
];

/**
 * The band a given active-subscriber count falls in. Both ends inclusive.
 *
 * 🔑 A NEW PROVIDER HAS NO SUBSCRIBERS, so this returns Launch for 0 — which is
 * the whole answer to "which band do I start in" and the reason the
 * registration screen needs no input for it.
 */
export function bandFor(activeSubscribers: number): TierBand {
  if (!Number.isFinite(activeSubscribers) || activeSubscribers < 0) {
    throw new Error(`active subscriber count must be a non-negative number, got ${activeSubscribers}`);
  }
  const band = TIER_BANDS.find(
    (b) => activeSubscribers >= b.from && (b.to === null || activeSubscribers <= b.to),
  );
  if (!band) {
    // Unreachable while the bands are contiguous from 0 with an unbounded top,
    // which the tests assert. Throwing rather than returning a default: a
    // silent fallback to the cheapest band is a billing error that looks like
    // a working screen.
    throw new Error(`no band covers ${activeSubscribers} -- the band table has a gap`);
  }
  return band;
}

/** The band a provider starts in on the day they register. */
export const STARTING_BAND = bandFor(0);

/**
 * What an operator on `band` would pay at `count` subscribers WITHOUT upgrading:
 * the plan price plus overage on everything above the allowance.
 *
 * ⛔ NOT A BILLING FUNCTION. No overage engine exists; this expresses the
 * published rate so the page can show why upgrading is always the cheaper
 * option, and so a test can prove it rather than the page asserting it.
 */
export function costOnOverage(band: TierBand, activeSubscribers: number): number | null {
  if (band.price === null || band.overagePerSubscriber === null || band.to === null) return null;
  const excess = Math.max(0, activeSubscribers - band.to);
  return band.price + excess * band.overagePerSubscriber;
}

/**
 * The ceiling on a month's bill: three times the plan price. The customer can
 * raise their own from the console, so it protects rather than blocks.
 */
export const BILL_CEILING_MULTIPLE = 3;

export function billCeiling(band: TierBand): number | null {
  return band.price === null ? null : band.price * BILL_CEILING_MULTIPLE;
}

/** ₹1,999 — grouping per the Indian numbering system, which is what an invoice uses. */
export function formatInr(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

/** "₹2.40" — overage is a per-subscriber rate, so it needs the paise. */
export function formatInrPaise(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** "0 – 500" / "25,001+" — the range as the table shows it. */
export function bandRange(b: TierBand): string {
  const n = (v: number) => new Intl.NumberFormat("en-IN").format(v);
  return b.to === null ? `${n(b.from)}+` : `${n(b.from)} – ${n(b.to)}`;
}
